import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {  Card, Container, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCheckCircle, faCloud, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import './VerificationPending.css';

const VerificationPending = () => {
  const { resendVerification } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    // Get email from location state or redirect to login
    if (location.state && location.state.email) {
      setEmail(location.state.email);
    } else {
      navigate('/login');
    }
  }, [location, navigate]);

  useEffect(() => {
    // Countdown timer for resend button
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Handle resend verification email
  const handleResendVerification = async () => {
    if (!email || loading || countdown > 0) return;

    setLoading(true);
    setError('');
    setMessage('');
    setResendSuccess(false);

    try {
      const response = await resendVerification(email);

      if (response.status === 200) {
        setResendSuccess(true);
        setMessage('Verification email sent successfully. Please check your inbox.');
        setResendCount(prevCount => prevCount + 1);
        setCountdown(60); // 60 seconds cooldown
      } else if (response.data?.already_verified) {
        setMessage('Your email is already verified. You can now login.');
      } else {
        setError(response.message || 'Failed to resend verification email');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="verification-container">
      <Container>
        <Row className="justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
          <Col md={8} lg={7} xl={6}>
            <Card className="verification-card">
              <div className="verification-header text-center">
                <FontAwesomeIcon icon={faCloud} className="cloud-icon" />
                <div className="brand-name">
                  <FontAwesomeIcon icon={faCloud} className="brand-name-icon" />
                  <h1>Document Review</h1>
                </div>
                <div className="app-name">Cloud Document Management System</div>
              </div>
              
              <div className="verification-body">
                <FontAwesomeIcon icon={faEnvelope} className="email-icon" />
                <h2 className="mb-3">Verify Your Email Address</h2>
                
                <div className="email-highlight">
                  {email}
                </div>
                
                <p className="mb-4">
                  We've sent a verification link to your email address. 
                  Please check your inbox and click the link to activate your account.
                </p>
                
                {message && (
                  <div className="success-message">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                    {message}
                  </div>
                )}
                
                {error && (
                  <div className="error-message">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-2" />
                    {error}
                  </div>
                )}
                
                <div className="mt-4">
                  <p className="mb-3">Didn't receive the email?</p>
                  <Button
                    className="verification-btn"
                    onClick={handleResendVerification}
                    disabled={loading || countdown > 0}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sending...
                      </>
                    ) : countdown > 0 ? (
                      `Resend in ${countdown}s`
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                        Resend Verification Email
                      </>
                    )}
                  </Button>
                </div>
                
                <div className="mt-4 pt-3">
                  <p className="text-muted">
                    Already verified your email? <span className="text-primary" style={{cursor: 'pointer'}} onClick={() => navigate('/login')}>Sign in here</span>
                  </p>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default VerificationPending;
